/**
 * The purpose of this class is handling of "touchstart", "touchmove" events
 * on target container to allow scrolling. In current implementation we have
 * handler on chat's container that blocks all "touchmove" events. To allow
 * scrolling inside transcript area or inside ACIF's automaton containers we
 * should detect coordinates of touch event and if they are within the container,
 * we call {@link Event.prototype.stopPropagation} to not trigger the parent handler,
 * otherwise we do nothing and during bubbling event will be blocked.
 *
 * @see com.inq.flash.client.chatskins.SkinControl.stopScrollPropagation
 * @param {HTMLElement} container - target container when scrolling should be allowed
 * @param {boolean=} useScrollForChildren - if specified, then scroll will be handled on
 * 											all scrollable container's children, otherwise
 * 											only on container itself.
 * @constructor
 */
com.inq.ui.NativeScroller = function (container, useScrollForChildren) {
	/** @type {HTMLElement} */
	this._container = container;
	/** @type {boolean} */
	this._useScrollForChildren = useScrollForChildren === true;
	/** @type {Array.<Element>} */
	this._scrollableElements = [];
	/** @type {MutationObserver} */
	this._mutationObserver = new MutationObserver(function() {
		this.updateScrollableElementsList();
	}.bind(this));
	/*
	 * Since calling of Function.prototype.bind creates new function instance,
	 * we need to rewrite these fields here, otherwise, we will can't remove
	 * event listeners in com.inq.ui.NativeScroller.prototype.cleanUp method.
	 */
	this.touchStartHandler = this.touchStartHandler.bind(this);
	this.touchMoveHandler = this.touchMoveHandler.bind(this);
	this.initNativeScrolling();
};

com.inq.ui.NativeScroller.prototype.applyOverflowStyle = function () {
	this._container.style.overflow = "scroll";
	this._container.style.webkitOverflowScrolling = "touch";
};

/**
 * Adds events listeners for "touchstart", "touchmove" events and activates {@link _mutationObserver}
 */
com.inq.ui.NativeScroller.prototype.initNativeScrolling = function() {
	com.inq.utils.Capabilities.BindListener(this._container, "touchstart", this.touchStartHandler);
	com.inq.utils.Capabilities.BindListener(this._container, "touchmove", this.touchMoveHandler);
	this._mutationObserver.observe(this._container, {
		childList: true,
		attributes: true,
		subtree: true
	});
};

/**
 * Updates list of scrollable child containers. If {@link _useScrollForChildren}
 * is true, than this method should be called every time when container's HTML
 * markup changes
 */
com.inq.ui.NativeScroller.prototype.updateScrollableElementsList = function() {
	if (this._useScrollForChildren) {
		this._scrollableElements = this._getScrollableChildren();
	} else {
		this._scrollableElements = [this._container];
	}
};

/**
 * Processes "touchstart" event and performs the following tasks:
 *   Saves coordinates of "touchstart" event
 *   Forces focus setting for iPhones
 * @param {TouchEvent} event
 */
com.inq.ui.NativeScroller.prototype.touchStartHandler = function(event) {
	/** @type {com.inq.utils.Point} */
	this.startLocation = this.getTouchLocation(event);
	com.inq.flash.client.chatskins.ChatTextFocusMonitorImplIphoneSafari.forceFocus(event);
};

/**
 * Stops propagation of "touchmove" event if it occurs inside the
 * scrollable container to not trigger the parent handler which blocks scroll
 * @see com.inq.flash.client.chatskins.SkinControl.stopScrollPropagation
 * @param {TouchEvent} event
 */
com.inq.ui.NativeScroller.prototype.touchMoveHandler = function(event) {
	for (var i = 0; i < this._scrollableElements.length; i++) {
		/** @type {Element} */
		var scrollableElement = this._scrollableElements[i];
		if (scrollableElement.contains(event.target)) {
			if (this._getSwipeDirection(event) === "vertical") {
				this._onVerticalSwipe(scrollableElement, event);
			} else {
				this._onHorizontalSwipe(scrollableElement, event);
			}
		}
	}
};

/**
 * Handles horizontal swipe and stops event propagation,
 * if scroll occurs inside target container.
 * @param {HTMLElement} scrollableElement
 * @param {TouchEvent} event
 * @private
 */
com.inq.ui.NativeScroller.prototype._onHorizontalSwipe = function(scrollableElement, event) {
	/** @type {com.inq.utils.Point} */
	var touchLocation = this.getTouchLocation(event);
	if (this.startLocation.x > touchLocation.x) {
		/* finger swipe left motion */
		if ((scrollableElement.scrollLeft + scrollableElement.clientWidth) < scrollableElement.scrollWidth) {
			event.stopPropagation();
		}
	} else {
		/* finger swipe right motion */
		if (scrollableElement.scrollLeft > 0) {
			event.stopPropagation();
		}
	}
};

/**
 * Handles vertical swipe and stops event propagation,
 * if scroll occurs inside target container.
 * @param {HTMLElement} scrollableElement
 * @param {TouchEvent} event
 * @private
 */
com.inq.ui.NativeScroller.prototype._onVerticalSwipe = function(scrollableElement, event) {
	/** @type {com.inq.utils.Point} */
	var touchLocation = this.getTouchLocation(event);
	if (this.startLocation.y > touchLocation.y) {
		/* finger swipe up motion */
		if ((scrollableElement.scrollTop + scrollableElement.clientHeight) < scrollableElement.scrollHeight) {
			event.stopPropagation();
		}
	} else {
		/* finger swipe down motion */
		if (scrollableElement.scrollTop > 0) {
			event.stopPropagation();
		}
	}
};

/**
 * Detects swipe direction (vertical or horizontal) by comparing the absolute values
 * of differences between starting and ending coordinates of touch event for X and Y axis.
 * @param {TouchEvent} event
 * @return {string} "vertical" or "horizontal"
 * @private
 */
com.inq.ui.NativeScroller.prototype._getSwipeDirection = function(event) {
	/** @type {com.inq.utils.Point} */
	var touchLocation = this.getTouchLocation(event);
	/** @type {number} */
	var xDiff = this.startLocation.x - touchLocation.x;
	/** @type {number} */
	var yDiff = this.startLocation.y - touchLocation.y;
	return Math.abs(yDiff) > Math.abs(xDiff) ? "vertical" : "horizontal";
};

/**
 * Checks all child elements and finds ones that have vertical or horizontal scrollbar
 * @return {Array<Element>}
 * @private
 */
com.inq.ui.NativeScroller.prototype._getScrollableChildren = function() {
	/** @type {HTMLCollection} */
	var childNodes = this._container.getElementsByTagName("*");
	/** @type {Array.<Element>} */
	var scrollableChildren = [];
	for (var i = 0; i < childNodes.length; i++) {
		/** @type {Element} */
		var node = childNodes[i];
		if (this._isScrollable(node)) {
			scrollableChildren.push(node);
		}
	}
	return scrollableChildren;
};

/**
 * Determines is element has vertical or horizontal scrollbar.
 * The approach is to write value to the element's scrollTop/scrollLeft
 * property and then read it. If the element is scrollable, then the written
 * value will be returned.
 * @param {Element} element
 * @return {boolean}
 * @private
 */
com.inq.ui.NativeScroller.prototype._isScrollable = function(element) {
	/** @type {boolean} */
	var hasVerticalScrollbar = testScroll(true);
	/** @type {boolean} */
	var hasHorizontalScrollbar = testScroll(false);
	return hasHorizontalScrollbar || hasVerticalScrollbar;

	function testScroll(vertical) {
		/** @type {string} */
		var direction = vertical ? "scrollTop" : "scrollLeft";
		/** @type {boolean} */
		var result = element[direction] !== 0;
		/*
		 * if scrollTop/scrollLeft value isn't 0, then we definitely know,
		 * that element is scrollable
		 */
		if (!result) {
			/*
			 * To reduce number of unnecessary scrolls, let's firstly check
			 * scrollHeight/scrollWidth and clientHeight/clientWidth properties
			 * for equality. In normal scenario if they are not equal, then element
			 * considered to be scrollable. But since ACIF has many automatons
			 * with incorrect markup, we can have case, when these properties
			 * are not equal, but element is still not scrollable. It can occurs,
			 * e.g. when element has a child which is larger than parent because
			 * of incorrect "padding" or "margin" properties. When child has "position: absolute"
			 * they will affect the scrollHeight property. Summing up, if test for
			 * equality failed, we also need to check read/write approach.
			 */
			if (vertical) {
				if (element.scrollHeight === element.clientHeight) {
					return false;
				}
			} else {
				if (element.scrollWidth === element.clientWidth) {
					return false;
				}
			}
			element[direction] = 1;
			result = element[direction] === 1;
			element[direction] = 0;
		}
		return result;
	}
};

/**
 * Removes "touchstart", "touchmove" event listeners from {@link _container} and
 * disconnects {@link _mutationObserver}
 */
com.inq.ui.NativeScroller.prototype.cleanUp = function() {
	com.inq.utils.Capabilities.UnbindListener(this._container, "touchstart", this.touchStartHandler);
	com.inq.utils.Capabilities.UnbindListener(this._container, "touchmove", this.touchMoveHandler);
	this._mutationObserver.disconnect();
};

/**
 * Returns coordinates of touch event
 * @param {TouchEvent} event
 * @return {com.inq.utils.Point}
 */
com.inq.ui.NativeScroller.prototype.getTouchLocation = function(event) {
	/** @type {Touch|TouchEvent} */
	var touch = (event.touches) ? event.touches[0] : event;
	return new com.inq.utils.Point(touch.screenX, touch.screenY);
};